import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUsers, createUser, updateUser, deleteUser, getMinistries } from '../services/api';
import './AdminUsers.css';

const AdminUsers = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user',
    ministries: []
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadUsers();
    loadMinistries();
  }, [isAdmin, navigate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setError('Erro ao carregar usuários.');
    } finally {
      setLoading(false);
    }
  };

  const loadMinistries = async () => {
    try {
      const response = await getMinistries(new Date().getFullYear());
      setMinistries(response.data);
    } catch (error) {
      console.error('Erro ao carregar ministérios:', error);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '',
        role: user.role,
        ministries: user.ministries.map(m => m._id)
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        role: 'user',
        ministries: []
      });
    }
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      role: 'user',
      ministries: []
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const submitData = {
        username: formData.username,
        role: formData.role,
        ministries: formData.ministries
      };

      // Only include password if it's provided
      if (formData.password) {
        submitData.password = formData.password;
      } else if (!editingUser) {
        setError('Senha é obrigatória para novos usuários.');
        setSubmitting(false);
        return;
      }

      if (editingUser) {
        await updateUser(editingUser._id, submitData);
      } else {
        await createUser(submitData);
      }
      await loadUsers();
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      setError(error.response?.data?.message || 'Erro ao salvar usuário.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, username, isMainAdmin) => {
    if (isMainAdmin) {
      alert('Não é possível deletar o administrador principal.');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja excluir o usuário "${username}"?`)) {
      return;
    }

    try {
      await deleteUser(id);
      await loadUsers();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      alert(error.response?.data?.message || 'Erro ao excluir usuário.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleMinistryToggle = (ministryId) => {
    setFormData(prev => ({
      ...prev,
      ministries: prev.ministries.includes(ministryId)
        ? prev.ministries.filter(id => id !== ministryId)
        : [...prev.ministries, ministryId]
    }));
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return <span className="badge badge-admin">Admin</span>;
    } else if (role === 'payer') {
      return <span className="badge badge-payer">Pagador</span>;
    } else if (role === 'secretary') {
      return <span className="badge badge-secretary">Secretaria</span>;
    } else {
      return <span className="badge badge-user">Usuário</span>;
    }
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1>Administração de Usuários</h1>
          <button onClick={() => handleOpenModal()} className="btn btn-primary">
            Adicionar Usuário
          </button>
        </div>

        {users.length === 0 ? (
          <div className="card empty-state">
            <p>Nenhum usuário cadastrado.</p>
            <button onClick={() => handleOpenModal()} className="btn btn-primary">
              Adicionar Primeiro Usuário
            </button>
          </div>
        ) : (
          <div className="users-table-container">
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Tipo</th>
                    <th>Ministérios</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td>
                        <strong>{user.username}</strong>
                        {user.isMainAdmin && (
                          <span className="badge badge-primary ml-2">Principal</span>
                        )}
                      </td>
                      <td>{getRoleBadge(user.role)}</td>
                      <td>
                        {user.ministries.length > 0 ? (
                          <div className="ministries-list">
                            {user.ministries.map(ministry => (
                              <span key={ministry._id} className="ministry-tag">
                                {ministry.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">Nenhum</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleOpenModal(user)}
                            className="btn btn-sm btn-secondary"
                            disabled={user.isMainAdmin}
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(user._id, user.username, user.isMainAdmin)}
                            className="btn btn-sm btn-danger"
                            disabled={user.isMainAdmin}
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingUser ? 'Editar Usuário' : 'Adicionar Usuário'}</h2>
                <button className="modal-close" onClick={handleCloseModal}>
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="username" className="form-label">
                      Nome de Usuário *
                    </label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      className="form-control"
                      value={formData.username}
                      onChange={handleChange}
                      required
                      placeholder="Ex: joao.silva"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="password" className="form-label">
                      Senha {editingUser ? '' : '*'}
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      className="form-control"
                      value={formData.password}
                      onChange={handleChange}
                      required={!editingUser}
                      placeholder={editingUser ? 'Deixe em branco para manter a senha atual' : 'Senha do usuário'}
                    />
                    {editingUser && (
                      <small className="form-help">
                        Deixe em branco para não alterar a senha
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="role" className="form-label">
                      Tipo de Acesso *
                    </label>
                    <select
                      id="role"
                      name="role"
                      className="form-control"
                      value={formData.role}
                      onChange={handleChange}
                      required
                    >
                      <option value="user">Usuário</option>
                      <option value="payer">Pagador</option>
                      <option value="secretary">Secretaria</option>
                      <option value="admin">Administrador</option>
                    </select>
                    <small className="form-help">
                      Pagador: visualiza e marca reembolsos como pagos. Secretaria: gerencia as votações de nomeação.
                    </small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Ministérios Associados
                    </label>
                    <div className="ministries-checkboxes">
                      {ministries.length === 0 ? (
                        <p className="text-muted">Nenhum ministério cadastrado</p>
                      ) : (
                        ministries.map(ministry => (
                          <label key={ministry._id} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.ministries.includes(ministry._id)}
                              onChange={() => handleMinistryToggle(ministry._id)}
                            />
                            <span>{ministry.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                    <small className="form-help">
                      Selecione os ministérios que este usuário pode acessar
                    </small>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn btn-secondary"
                    disabled={submitting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
